<template>
<div v-if="status.requestInProgress">
	<v-dialog
	v-model="visible"
	width="300px"
	hide-overlay
	>
		<v-card
			color="primary"
			dark
		>
			<v-card-text>
			<template>
				In progress …
			</template>
			<v-progress-linear
				indeterminate
				color="white"
				class="mb-0"
			></v-progress-linear>
			</v-card-text>
		</v-card>

	</v-dialog>
</div>
<div v-else>
	<v-dialog v-model="visible" persistent max-width="800px">
		<v-card>
			<v-card-title class="headline">
				<div>
					Recording
				</div>
			</v-card-title>
			<v-container>
				<v-form ref="form">
				<v-layout row wrap>
					<v-flex xs12>
						<v-alert
						:value="status.error"
						type="error"
						icon="warning"
						>
						{{status.error}}
						</v-alert>
					</v-flex>
					<v-flex xs12 sm5>
						<v-text-field v-if="!writableTokens.length"
						label="Token name"
						hint="Enter the name for the token where data will be saved"
						v-model="tokenName"
						:rules="[...requiredRules]"
						required
						:disabled="isRecording"
						></v-text-field>
						
						<v-select v-if="writableTokens.length"
						chips
						label="Token"
						v-model="token_id"
						:items="writableTokens"
						item-text="metadata.name"
						item-value="token_id"
						item-avatar="metadata.imageUrl"
						:disabled="isRecording"
						></v-select>
					</v-flex>
					<v-flex xs12 sm5 offset-sm2>
						<v-text-field
						label="Recording interval"
						type="number"
						suffix=" s"
						v-model="recordingInterval"
						min="1"
						required
						:disabled="isRecording"
						></v-text-field>
					</v-flex>
					<v-flex xs12 sm5>
						<v-text-field
						label="Minimum accuracy"
						hint="Will not record a point with lesser accuracy than this value"
						type="number"
						suffix=" m"
						v-model="minAccuracyValue"
						min="1"
						append-icon="clear"
						@click:append="() => minAccuracyValue = null"
						:disabled="isRecording"
						></v-text-field>
					</v-flex>
					<v-flex xs12 sm5 offset-sm2>
						<v-text-field
						label="Minimum distance"
						hint="Will not record a point if it is less than this distance from the last recorded position"
						type="number"
						suffix=" m"
						v-model="minDistanceValue"
						min="1"
						append-icon="clear"
						@click:append="() => minDistanceValue = null"
						:disabled="isRecording"
						></v-text-field>
					</v-flex>
					<v-flex xs12 sm5 offset-sm7>
						<v-text-field
						label="Minimum speed"
						hint="Will not record a point below this speed"
						type="number"
						v-model="minSpeedValue"
						min="1"
						append-icon="clear"
						@click:append="() => minSpeedValue = null"
						:disabled="isRecording"
						>
							<v-select
							style="top: -20px; width: 4em;"
							slot="append-outer"
							v-model="speedUnits"
							:items="speedUnitsList"
							></v-select>
						</v-text-field>
					</v-flex>
				</v-layout>
				</v-form>
			</v-container>
				<v-card-actions v-if="isRecording">
					<v-btn flat @click="dismiss">Close</v-btn>
				
					<v-spacer></v-spacer>
					
					<v-chip
					outline
					:color="savedCount? 'blue' : 'blue lighten-3'">
						<v-avatar>
							<v-icon small>fa-upload</v-icon>
						</v-avatar>
						{{savedCount}} <span class="hidden-xs-only"> fixes</span>
					</v-chip>
					
					<v-spacer></v-spacer>
					
					<v-btn flat color="primary" @click="stop" :disabled="!formValid">
						Stop
					</v-btn>
				</v-card-actions>
				<v-card-actions v-else>
					<v-btn flat @click="dismiss">Close</v-btn>
				
					<v-spacer></v-spacer>
					
					<v-btn flat color="primary" @click="start" :disabled="!formValid">
						Start
					</v-btn>
				</v-card-actions>
		</v-card>
	</v-dialog>
</div>
</template>

<script>
import Vuex from 'vuex'
import * as moment from 'moment'
import * as GeographicLib from 'geographiclib'
import api from '../../assets/js/api-instance'

export default {
	name: "FollowRecordingOptions",
	
	props: ["show"],

	data () {
		return {
			isRecording: false,
			token_id: null,
			tokenName: null,
			recordingInterval: 10,
			minAccuracyValue: null,
			minDistanceValue: null,
			minSpeedValue: null,
			speedUnits: "m/s",
			speedUnitsList: [ "m/s", "km/h", "kt", "mph" ],
			requiredRules: [
			],
			
			timer: null,
			savedCount: 0,
			
			status: {}
		};
	},
	
	watch: {
		show () {
			if (this.show instanceof Object) {
				this.setData(this.show);
			} else if (typeof this.show == 'string') {
				const token = this.$store.state.tokenData.tokens.find(t => t.token_id == this.show);
				this.setData(token);
				if (!token) {
					console.warn("Token editor: received token ID but cannot find this token", this.show);
				}
			} else {
				this.setData({});
			}
		},
		
		writableTokens () {
			if (this.token_id) {
				// If our token disappears, stop recording
				if (this.isRecording && !this.writableTokens.includes(this.token_id)) {
					console.warn("Our token has disappeared. Stopped recording");
					this.stop();
				}
			} else {
				if (this.writableTokens.length == 1) {
					// If there is only one writable token, preselect it
					this.token_id = this.writableTokens[0];
				}
			}
		}
	},
	
	computed: {
		...Vuex.mapState(["tokenData"]),
		
		visible: {
			get () {
				return !!this.show;
			},
			set () {
				this.$emit('hide')
			}
		},
		
		writableTokens () {
			return this.tokenData.tokens.filter(t => t.permissions.includes("c"));
		},
		
		formValid () {
			return this.token_id && this.recordingInterval > 0;
		},
		
		minSpeedValueMetresPerSecond () {
			switch (this.speedUnits) {
				case null:
				case "m/s":
					return this.minSpeedValue;
					break;
				case "km/h":
					return this.minSpeedValue / 3.6;
					break;
				case "kt":
					return this.minSpeedValue / 1.852 / 3.6;
					break;
				case "mph":
					return this.minSpeedValue / 1.609344 / 3.6;
					break;
				default:
					throw new Error("Unknown speed units", this.speedUnits);
			}
		}
	},

	methods: {
		setData (obj) {
			if (obj && obj.token_id) {
				this.token_id = obj.token_id;
			} else if (this.writableTokens.length == 1) {
				this.token_id = this.writableTokens[0].token_id;
			}
		},
		
		validate () {
			this.$refs["token-share"].validate();
		},
		
		async dismiss () {
			this.setData({});
			this.$emit("hide");
		},
		
		async start () {
			if (this.formValid) {
				this.isRecording = true;
				this.$store.commit('setTokenRecording', {token_id: this.token_id, recording: this.isRecording});
				this.savedCount = 0;
				this.record();
				this.dismiss();
			}
		},
		
		async stop () {
			clearInterval(this.timer);
			navigator.geolocation.clearWatch(this.watch);
			this.isRecording = false;
			this.$store.commit('setTokenRecording', {token_id: this.token_id, recording: this.isRecording});
		},
		
		async record () {
			console.log("Called record()");
			const geod = GeographicLib.Geodesic.WGS84;
			
			let lastRecordedPosition;
			
			if (!("geolocation" in navigator)) {
				console.warn("Geolocation API is not available");
				return;
			}
			
			const positionOptions = {
				enableHighAccuracy: true,
				maximumAge: 0
			};
			console.log("positionOptions", positionOptions);
			
			const positionError = (error) => {
				console.warn("Position API error:", error);
				if (error && error.code == 1) {
					// Not authorised to use location, probably not secure context
					const event = new CustomEvent('follow-error', {
						detail: {
							originalError: error,
							message: "Not authorised to request location (Are you using HTTPS?)",
							type: "error"
						}
					});
					api.eventTarget.dispatchEvent(event);
					this.stop();
				}
			};
			
			const processPosition = async (position) => {
				console.log("Called processPosition", position);
				
				if (!position) {
					console.log("No position");
					return;
				}
				
				if (lastRecordedPosition && position.timestamp == lastRecordedPosition.timestamp) {
					console.log("Same timestamp as last position", lastRecordedPosition, position);
					return; // This is not a new fix
				}
				
				if (this.minAccuracyValue && position.coords.accuracy > this.minAccuracyValue) {
					console.log("Too inaccurate", position.coords.accuracy);
					return; // Too inaccurate, ignore.
				}
				
				if (this.minSpeedValue && position.coords.speed < this.minSpeedValueMetresPerSecond) {
					console.log("Too slow", position.coords.speed);
					return; // Too slow
				}
				
				const vector = lastRecordedPosition
					? geod.Inverse(
						lastRecordedPosition.coords.latitude,
						lastRecordedPosition.coords.longitude,
						position.coords.latitude,
						position.coords.longitude
					)
					: {};
				
				if (lastRecordedPosition &&
					this.minDistanceValue &&
					vector.s12 < this.minDistanceValue) {
					console.log("Too close", vector);
					return; // Too close
				}
				
				const fixData = {
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					altitude: position.coords.altitude,
					speed: position.coords.speed,
					direction: position.coords.heading,
					timestamp: position.timestamp,
					metadata: {
						accuracy_h: position.coords.accuracy,
						accuracy_v: position.coords.altitudeAccuracy
					}
				};
				
				try {
					console.log("Saving position", position);
					await api.saveFix(this.token_id, fixData);
					lastRecordedPosition = position;
					this.savedCount++;
					console.log("Saved", this.savedCount);
				} catch (err) {
					console.warn("Error saving fix", err);
				}
				
			};
			
			const positionWatcher = () => {
				console.log("Get discrete position");
				navigator.geolocation.getCurrentPosition(processPosition, positionError, positionOptions);
			};
			
			clearInterval(this.timer);
			positionWatcher();
			this.timer = setInterval(positionWatcher, this.recordingInterval*1000);
		}
	}
	
}
</script>
